/**
 * useRoleAccess Hook
 * Custom React hook for checking role-based access to modules and features
 *
 * Phase 1: Component-level access control
 * Phase 2: Feature-level access control (prepared)
 */

/**
 * Role-to-Module mapping (Phase 1)
 * Defines which modules each role can access
 */
const MODULE_ACCESS = {
  superadmin: [
    'dashboard',
    'devices',
    'complaints',
    'analytics',
    'alerts',
    'users',
    'settings',
    'audit_viewer',
    'locations',
    'departments',
    'device_names',
    'complaint_categories',
    'superadmin',
    'device_transfers',
  ],
  admin: [
    'dashboard',
    'devices',
    'complaints',
    'analytics',
    'alerts',
    'users',
    'settings',
    'audit_viewer',
    'device_transfers',
    // Admin has full access to all location-level features
    // Cannot access superadmin features (locations, departments, device_names)
  ],
  coordinator: [
    'dashboard',
    'devices',
    'complaints',
    'analytics',
    'alerts'
  ],
  custodian: [
    'dashboard',
    'devices',
    'complaints',
    'analytics',
    'alerts',
    'department_transfers',
  ],
  manager: [
    'dashboard',
    'devices',
    'complaints',
    'analytics',
    'alerts',
    'users',  // Need to view user names for technician assignment and complaint notes
    'device_transfers',
  ],
  technician: [
    'dashboard',
    'devices_view',
    'alerts',
    'complaints',
    'analytics',
    'maintenance',
    'device_transfers',
  ],
  central_help_desk: [
    'dashboard',
    'devices',
    'complaints',
    'alerts'
  ]
};

/**
 * Feature-to-Role mapping (Phase 2 - prepared)
 * Defines which roles can perform which features
 *
 * COORDINATOR RESTRICTIONS:
 * - Cannot create/edit/delete devices
 * - Cannot assign complaints
 * - Cannot update complaint status (close/cancel)
 * - Cannot access financial data
 */
const FEATURE_ACCESS = {
  // Device operations - aligned with backend devices.py
  // Technician can view devices but not create/edit/delete
  // Coordinator: view only, no create/edit/delete
  device_create: ['admin', 'superadmin', 'manager'],
  device_edit: ['admin', 'superadmin', 'manager'],
  device_delete: ['admin', 'superadmin'],
  device_scan_qr: ['superadmin', 'admin', 'coordinator', 'custodian', 'manager', 'technician'],
  device_upload_documents: ['admin', 'superadmin', 'manager'],

  // Export operations - aligned with backend exports.py & EXPORT_ACCESS whitelist
  export_devices: ['superadmin', 'admin', 'coordinator', 'custodian', 'manager', 'central_help_desk'],
  export_complaints: ['superadmin', 'admin', 'coordinator', 'custodian', 'manager', 'central_help_desk'],

  // Complaint operations
  // Coordinator: can create complaints but NOT assign, close, or cancel
  // Manager: can assign, close, cancel complaints but CANNOT change status (open→in_progress requires device scan by technician)
  complaint_create: ['admin', 'coordinator', 'manager', 'technician', 'central_help_desk', 'custodian'],
  complaint_assign: ['admin', 'manager', 'central_help_desk'],
  complaint_close: ['admin', 'manager', 'technician', 'central_help_desk'],
  complaint_cancel: ['admin', 'manager', 'technician', 'central_help_desk'],
  complaint_change_status: ['admin', 'technician', 'central_help_desk'],  // Manager CANNOT - only technician via device scan

  // Financial data access - RESTRICTED for coordinator
  financial_view: ['admin', 'superadmin', 'manager'],
  financial_edit: ['admin', 'superadmin'],

  // User management - superadmin only for certain operations
  user_create: ['admin', 'superadmin'],
  user_edit: ['admin', 'superadmin'],
  user_delete: ['admin', 'superadmin'],
  user_assign_role: ['admin', 'superadmin'],

  // Admin operations - superadmin only
  location_create: ['superadmin'],
  location_edit: ['superadmin'],
  location_delete: ['superadmin'],
  department_create: ['superadmin'],
  department_edit: ['superadmin'],
  department_delete: ['superadmin'],
  device_names_edit: ['superadmin']
};

/**
 * Main hook for role-based access control
 *
 * @param {Object} user - User object containing role and other info
 * @returns {Object} Object with helper functions for access checking
 */
export function useRoleAccess(user) {
  /**
   * Phase 1: Check if user can access a module
   */
  const canAccessModule = (moduleName, hideInNavbar = false) => {
    if (!user || !user.role) {
      return false;
    }

    // Manager: has backend module access to 'users' for internal use (complaint assignment)
    // but NOT shown in navbar
    if (hideInNavbar && user.role === 'manager' && moduleName === 'users') {
      return false;
    }

    const accessibleModules = MODULE_ACCESS[user.role] || [];
    return accessibleModules.includes(moduleName);
  };

  /**
   * Get all modules accessible by current user
   */
  const getAccessibleModules = () => {
    if (!user || !user.role) {
      return [];
    }

    return MODULE_ACCESS[user.role] || [];
  };

  /**
   * Phase 2: Check if user can perform a feature
   */
  const canPerformFeature = (featureName) => {
    if (!user || !user.role) {
      return false;
    }

    const allowedRoles = FEATURE_ACCESS[featureName] || [];
    return allowedRoles.includes(user.role);
  };

  /**
   * Check if user has admin privilege
   */
  const isAdmin = () => {
    return user && (user.role === 'admin' || user.role === 'superadmin');
  };

  /**
   * Check if user is superadmin
   */
  const isSuperAdmin = () => {
    return user && user.role === 'superadmin';
  };

  /**
   * Check if user is manager level or above
   */
  const isManagerLevel = () => {
    return user && ['superadmin', 'admin', 'coordinator', 'manager'].includes(user.role);
  };

  /**
   * Check if user is technical staff
   */
  const isTechnicalStaff = () => {
    return user && ['manager', 'technician'].includes(user.role);
  };

  /**
   * Get user's role
   */
  const getUserRole = () => {
    return user ? user.role : null;
  };

  /**
   * Get user's role in display format
   */
  const getRoleDisplayName = () => {
    const roleNames = {
      superadmin: 'System Administrator',
      admin: 'Location Administrator',
      coordinator: 'Coordinator',
      custodian: 'Custodian',
      manager: 'Manager',
      technician: 'Technician',
      central_help_desk: 'Central Help Desk'
    };

    return roleNames[user?.role] || user?.role || 'Unknown';
  };

  return {
    // Phase 1: Module access
    canAccessModule,
    getAccessibleModules,

    // Phase 2: Feature access
    canPerformFeature,

    // Role checking utilities
    isAdmin,
    isSuperAdmin,
    isManagerLevel,
    isTechnicalStaff,
    getUserRole,
    getRoleDisplayName,

    // For debugging
    userRole: user?.role,
    isAuthenticated: !!user
  };
}

/**
 * Helper component: ProtectedModule
 * Wraps a component and only shows it if user has access
 *
 * Usage:
 * <ProtectedModule moduleName="users" user={user}>
 *   <UsersComponent />
 * </ProtectedModule>
 */
export function ProtectedModule({ moduleName, user, children, fallback = null }) {
  const { canAccessModule } = useRoleAccess(user);

  if (!canAccessModule(moduleName)) {
    return fallback || null;
  }

  return children;
}

/**
 * Helper component: ProtectedAction
 * Wraps a button/action and only shows it if user can perform it
 *
 * Usage:
 * <ProtectedAction featureName="device_create" user={user}>
 *   <button>Add Device</button>
 * </ProtectedAction>
 */
export function ProtectedAction({ featureName, user, children, fallback = null }) {
  const { canPerformFeature } = useRoleAccess(user);

  if (!canPerformFeature(featureName)) {
    return fallback || null;
  }

  return children;
}

/**
 * Helper function to get navigation items for a user
 * Returns filtered navigation items based on user role
 * NOTE: This is a regular function (not a hook) - it uses MODULE_ACCESS directly
 */
export function getNavigationItems(user) {
  // Check module access directly without using hooks
  const canAccessModule = (moduleName) => {
    if (!user || !user.role) {
      return false;
    }

    // Manager: has backend module access to 'users' for internal use (complaint assignment)
    // but NOT shown in navbar
    if (user.role === 'manager' && moduleName === 'users') {
      return false;
    }

    const accessibleModules = MODULE_ACCESS[user.role] || [];
    return accessibleModules.includes(moduleName);
  };

  const allNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '🏠' },
    { id: 'locations', label: 'Locations', icon: '📍' },
    { id: 'devices', label: 'Devices', icon: '🖥️' },
    { id: 'complaints', label: 'Complaints', icon: '💬' },
    { id: 'alerts', label: 'Alerts', icon: '🔔' },
    { id: 'analytics', label: 'Analytics', icon: '📊' },
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'departments', label: 'Departments', icon: '🏢' },
    { id: 'device_names', label: 'Device Names', icon: '🏥' },
    { id: 'complaint_categories', label: 'Complaint Categories', icon: '🗂️' },
    { id: 'data-import', label: 'Data Import', icon: '📥' },
    { id: 'audit_viewer', label: 'Audit Trail', icon: '📜' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
    { id: 'superadmin', label: 'System Overview', icon: '📊' }
  ];

  return allNavItems.filter(item => canAccessModule(item.id));
}

export default useRoleAccess;
